// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/pages/services/sapori/engine.ts
// 🔧 PURPOSE: Le regole di "Spara Sapori" (il gioco delle bolle), senza
//   disegni né React.
//   - Il tavolo è a nido d'ape: righe da 8 bolle e da 7 (spostate di mezza
//     bolla), il cielo in alto. Misure in "raggi di bolla": R = 1, largo 16.
//   - Si lancia da sotto con un angolo; la bolla rimbalza sui bordi e si
//     ferma toccando un'altra bolla (o il cielo), nella cella libera più vicina.
//   - 3 o più dello stesso sapore attaccate scoppiano (10 punti l'una); quelle
//     rimaste appese a niente cadono (20 punti l'una); tavolo vuoto +500.
//   - Ogni N tiri senza scoppi il cielo scende di una riga (N cala con i punti).
//   - Se una bolla arriva alla linea in basso, la partita è finita.
// ============================================

export const FLAVORS = ['lemon', 'olive', 'tomato', 'sea', 'fig', 'burrata'] as const;
export type Flavor = (typeof FLAVORS)[number];

export const COLS = 8;
export const R = 1;
export const WIDTH = COLS * 2 * R; // 16
export const ROW_H = Math.sqrt(3) * R;
/** Dalla riga DANGER_ROW in giù si perde. */
export const DANGER_ROW = 11;
export const DANGER_Y = R + DANGER_ROW * ROW_H;
/** Il lanciatore (il gozzo), sotto la linea. */
export const LAUNCH_X = WIDTH / 2;
export const LAUNCH_Y = DANGER_Y + 2.6;
export const HEIGHT = LAUNCH_Y + 2.4;
export const START_ROWS = 5;

export type Cell = Flavor | null;
export type Random = () => number;

export interface GameState {
  rows: Cell[][];
  /** La riga 0 è spostata di mezza bolla? (cambia quando il cielo scende). */
  shift: 0 | 1;
  current: Flavor;
  next: Flavor;
  score: number;
  shots: number;
  /** Tiri di fila senza scoppi. */
  misses: number;
  /** La raffica più grande (bolle tolte in un tiro). */
  bestBurst: number;
  over: boolean;
}

export interface Placed {
  row: number;
  col: number;
}

export interface ShotResult {
  state: GameState;
  /** Dove si è fermata la bolla lanciata. */
  placed: Placed;
  popped: Array<Placed & { flavor: Flavor }>;
  dropped: Array<Placed & { flavor: Flavor }>;
  gained: number;
  /** Il cielo è sceso di una riga. */
  descended: boolean;
  /** Tavolo ripulito. */
  cleared: boolean;
}

// ---- Geometria -------------------------------------------------------------------------

export const isShifted = (shift: 0 | 1, row: number) => (row + shift) % 2 === 1;
export const rowLength = (shift: 0 | 1, row: number) => (isShifted(shift, row) ? COLS - 1 : COLS);

export function cellCenter(shift: 0 | 1, row: number, col: number): { x: number; y: number } {
  return { x: R + col * 2 * R + (isShifted(shift, row) ? R : 0), y: R + row * ROW_H };
}

export function neighbors(shift: 0 | 1, row: number, col: number): Placed[] {
  const out: Placed[] = [{ row, col: col - 1 }, { row, col: col + 1 }];
  // In una riga spostata le vicine sopra/sotto sono col e col+1; altrimenti col-1 e col.
  const [a, b] = isShifted(shift, row) ? [col, col + 1] : [col - 1, col];
  for (const r of [row - 1, row + 1]) {
    out.push({ row: r, col: a }, { row: r, col: b });
  }
  return out.filter((p) => p.row >= 0 && p.col >= 0 && p.col < rowLength(shift, p.row));
}

const at = (s: Pick<GameState, 'rows'>, p: Placed): Cell => s.rows[p.row]?.[p.col] ?? null;

// ---- Partita nuova ---------------------------------------------------------------------

/** I sapori in gioco: 4 all'inizio, il fico da 1.500 punti, la burrata da 4.000. */
export function paletteFor(score: number): Flavor[] {
  return FLAVORS.slice(0, score >= 4000 ? 6 : score >= 1500 ? 5 : 4) as Flavor[];
}

/** Tiri a vuoto prima che scenda il cielo: 6, poi 5 (3.000 punti), poi 4 (8.000). */
export const missLimit = (score: number) => (score >= 8000 ? 4 : score >= 3000 ? 5 : 6);

const pick = <T,>(list: readonly T[], rnd: Random): T => list[Math.min(list.length - 1, Math.floor(rnd() * list.length))];

function randomRow(len: number, palette: Flavor[], rnd: Random): Cell[] {
  // Un po' di gruppi già fatti: metà delle volte ripete il sapore di prima.
  const row: Cell[] = [];
  for (let c = 0; c < len; c += 1) row.push(c > 0 && rnd() < 0.45 ? row[c - 1] : pick(palette, rnd));
  return row;
}

/** I sapori ancora sul tavolo (la prossima bolla è sempre uno di questi). */
export function flavorsOnBoard(s: Pick<GameState, 'rows'>): Flavor[] {
  const set = new Set<Flavor>();
  for (const row of s.rows) for (const c of row) if (c) set.add(c);
  return FLAVORS.filter((f) => set.has(f));
}

function nextFlavor(s: Pick<GameState, 'rows' | 'score'>, rnd: Random): Flavor {
  const onBoard = flavorsOnBoard(s);
  return pick(onBoard.length ? onBoard : paletteFor(s.score), rnd);
}

export function newGame(rnd: Random = Math.random): GameState {
  const palette = paletteFor(0);
  const rows: Cell[][] = [];
  for (let r = 0; r < START_ROWS; r += 1) rows.push(randomRow(rowLength(0, r), palette, rnd));
  const base = { rows, shift: 0 as const, score: 0 };
  return {
    ...base,
    current: nextFlavor(base, rnd),
    next: nextFlavor(base, rnd),
    shots: 0,
    misses: 0,
    bestBurst: 0,
    over: false,
  };
}

// ---- Il tiro ---------------------------------------------------------------------------

export interface Trace {
  /** I punti della traiettoria (il lancio, i rimbalzi, l'arrivo). */
  points: Array<{ x: number; y: number }>;
  /** La cella dove si ferma; null se non c'è posto. */
  target: Placed | null;
}

/** Angolo (radianti, 0 = destra, π/2 = su) da un punto toccato: mai troppo orizzontale. */
export function aimAngle(x: number, y: number): number | null {
  const dx = x - LAUNCH_X;
  const dy = LAUNCH_Y - y;
  if (dy < 0.5) return null;
  const a = Math.atan2(dy, dx);
  const min = (10 * Math.PI) / 180;
  return Math.min(Math.PI - min, Math.max(min, a));
}

/** La cella libera più vicina a (x, y), attaccata al cielo o a un'altra bolla. */
function snapCell(s: Pick<GameState, 'rows' | 'shift'>, x: number, y: number, reach = 1): Placed | null {
  const r0 = Math.round((y - R) / ROW_H);
  let best: Placed | null = null;
  let bestD = Infinity;
  for (let row = Math.max(0, r0 - reach); row <= r0 + reach; row += 1) {
    for (let col = 0; col < rowLength(s.shift, row); col += 1) {
      if (at(s, { row, col })) continue;
      const attached = row === 0 || neighbors(s.shift, row, col).some((n) => at(s, n));
      if (!attached) continue;
      const c = cellCenter(s.shift, row, col);
      const d = (c.x - x) ** 2 + (c.y - y) ** 2;
      if (d < bestD) {
        bestD = d;
        best = { row, col };
      }
    }
  }
  // Tutto pieno lì vicino (capita di rado): si guarda un po' più in là.
  return best || (reach < 3 ? snapCell(s, x, y, reach + 1) : null);
}

/** Dove va la bolla lanciata con quell'angolo (serve anche alla linea della mira). */
export function traceShot(s: Pick<GameState, 'rows' | 'shift'>, angle: number): Trace {
  const STEP = 0.08;
  let x = LAUNCH_X;
  let y = LAUNCH_Y;
  let dx = Math.cos(angle);
  const dy = -Math.sin(angle);
  const points = [{ x, y }];
  const bubbles: Array<{ x: number; y: number }> = [];
  s.rows.forEach((row, r) => row.forEach((c, col) => {
    if (c) bubbles.push(cellCenter(s.shift, r, col));
  }));
  const hit2 = (2 * R * 0.86) ** 2; // un po' di tolleranza: si "attacca" prima di toccare del tutto
  for (let i = 0; i < 4000; i += 1) {
    x += dx * STEP;
    y += dy * STEP;
    if (x < R) {
      x = 2 * R - x;
      dx = -dx;
      points.push({ x: R, y });
    } else if (x > WIDTH - R) {
      x = 2 * (WIDTH - R) - x;
      dx = -dx;
      points.push({ x: WIDTH - R, y });
    }
    let touches = y <= R;
    for (let b = 0; !touches && b < bubbles.length; b += 1) touches = (bubbles[b].x - x) ** 2 + (bubbles[b].y - y) ** 2 < hit2;
    if (touches) {
      const target = snapCell(s, x, Math.max(R, y));
      if (target) points.push(cellCenter(s.shift, target.row, target.col));
      return { points, target };
    }
  }
  return { points, target: null };
}

function groupOf(s: Pick<GameState, 'rows' | 'shift'>, start: Placed): Placed[] {
  const flavor = at(s, start);
  if (!flavor) return [];
  const seen = new Set([`${start.row},${start.col}`]);
  const out: Placed[] = [start];
  for (let i = 0; i < out.length; i += 1) {
    for (const n of neighbors(s.shift, out[i].row, out[i].col)) {
      const key = `${n.row},${n.col}`;
      if (!seen.has(key) && at(s, n) === flavor) {
        seen.add(key);
        out.push(n);
      }
    }
  }
  return out;
}

/** Le bolle non più collegate al cielo. */
function floating(s: Pick<GameState, 'rows' | 'shift'>): Placed[] {
  const seen = new Set<string>();
  const queue: Placed[] = [];
  (s.rows[0] || []).forEach((c, col) => {
    if (c) {
      seen.add(`0,${col}`);
      queue.push({ row: 0, col });
    }
  });
  for (let i = 0; i < queue.length; i += 1) {
    for (const n of neighbors(s.shift, queue[i].row, queue[i].col)) {
      const key = `${n.row},${n.col}`;
      if (!seen.has(key) && at(s, n)) {
        seen.add(key);
        queue.push(n);
      }
    }
  }
  const out: Placed[] = [];
  s.rows.forEach((row, r) => row.forEach((c, col) => {
    if (c && !seen.has(`${r},${col}`)) out.push({ row: r, col });
  }));
  return out;
}

const trimRows = (rows: Cell[][]) => {
  while (rows.length > 0 && rows[rows.length - 1].every((c) => !c)) rows.pop();
  return rows;
};

export const isLost = (s: Pick<GameState, 'rows'>) => s.rows.some((row, r) => r >= DANGER_ROW && row.some(Boolean));

/** Lancia la bolla corrente con quell'angolo. null se non c'è dove fermarla. */
export function shoot(state: GameState, angle: number, rnd: Random = Math.random): ShotResult | null {
  if (state.over) return null;
  const { target } = traceShot(state, angle);
  return target ? settle(state, target, rnd) : null;
}

/** La bolla corrente si ferma in quella cella: scoppi, cadute, punti, cielo. */
export function settle(state: GameState, target: Placed, rnd: Random = Math.random): ShotResult {
  const rows = state.rows.map((row) => [...row]);
  while (rows.length <= target.row) rows.push(new Array(rowLength(state.shift, rows.length)).fill(null));
  rows[target.row][target.col] = state.current;
  let s: GameState = { ...state, rows, shots: state.shots + 1 };

  const popped: ShotResult['popped'] = [];
  const dropped: ShotResult['dropped'] = [];
  const group = groupOf(s, target);
  if (group.length >= 3) {
    for (const p of group) {
      popped.push({ ...p, flavor: rows[p.row][p.col] as Flavor });
      rows[p.row][p.col] = null;
    }
    for (const p of floating(s)) {
      dropped.push({ ...p, flavor: rows[p.row][p.col] as Flavor });
      rows[p.row][p.col] = null;
    }
  }
  trimRows(rows);
  let gained = popped.length * 10 + dropped.length * 20;
  const cleared = popped.length > 0 && rows.length === 0;
  if (cleared) gained += 500;

  let misses = popped.length > 0 ? 0 : state.misses + 1;
  let descended = false;
  let shift = state.shift;
  const score = state.score + gained;
  if (cleared) {
    // Tavolo vuoto: tre righe nuove.
    for (let r = 0; r < 3; r += 1) rows.push(randomRow(rowLength(shift, r), paletteFor(score), rnd));
  } else if (misses >= missLimit(score)) {
    // Il cielo scende: una riga nuova in cima, le altre restano dove sono.
    shift = (shift === 0 ? 1 : 0) as 0 | 1;
    rows.unshift(randomRow(rowLength(shift, 0), paletteFor(score), rnd));
    misses = 0;
    descended = true;
  }

  s = {
    ...s,
    rows,
    shift,
    score,
    misses,
    bestBurst: Math.max(state.bestBurst, popped.length + dropped.length),
    current: state.next,
    next: state.next,
  };
  // La prossima solo tra i sapori ancora sul tavolo (anche quella già pronta, se è sparito il suo).
  const onBoard = flavorsOnBoard(s);
  if (onBoard.length && !onBoard.includes(s.current)) s.current = nextFlavor(s, rnd);
  s.next = nextFlavor(s, rnd);
  s.over = isLost(s);
  return { state: s, placed: target, popped, dropped, gained, descended, cleared };
}

/** Scambia la bolla pronta con quella di riserva. */
export const swap = (s: GameState): GameState => (s.over ? s : { ...s, current: s.next, next: s.current });

// ---- Salvataggio --------------------------------------------------------------------------

const SAVE_KEY = 'nva.sapori.game.v1';
const BEST_KEY = 'nva.sapori.best.v1';

export function saveGame(s: GameState): void {
  try {
    window.localStorage.setItem(SAVE_KEY, JSON.stringify(s));
  } catch {
    // niente
  }
}

const isFlavor = (f: unknown): f is Flavor => (FLAVORS as readonly string[]).includes(String(f));

export function loadGame(): GameState | null {
  try {
    const raw = JSON.parse(window.localStorage.getItem(SAVE_KEY) || 'null');
    if (!raw || !Array.isArray(raw.rows) || !isFlavor(raw.current) || !isFlavor(raw.next)) return null;
    const shift = raw.shift === 1 ? 1 : 0;
    const rows: Cell[][] = raw.rows.slice(0, DANGER_ROW + 1).map((row: unknown[], r: number) =>
      Array.from({ length: rowLength(shift, r) }, (_, c) => (isFlavor(row?.[c]) ? (row[c] as Flavor) : null)),
    );
    const s: GameState = {
      rows: trimRows(rows),
      shift,
      current: raw.current,
      next: raw.next,
      score: Math.max(0, Number(raw.score) || 0),
      shots: Math.max(0, Number(raw.shots) || 0),
      misses: Math.max(0, Number(raw.misses) || 0),
      bestBurst: Math.max(0, Number(raw.bestBurst) || 0),
      over: false,
    };
    s.over = isLost(s);
    return s.rows.length ? s : null;
  } catch {
    return null;
  }
}

export function clearSavedGame(): void {
  try {
    window.localStorage.removeItem(SAVE_KEY);
  } catch {
    // niente
  }
}

export function loadBest(): number {
  try {
    return Number(window.localStorage.getItem(BEST_KEY)) || 0;
  } catch {
    return 0;
  }
}

export function saveBest(score: number): void {
  try {
    if (score > loadBest()) window.localStorage.setItem(BEST_KEY, String(score));
  } catch {
    // niente
  }
}
